import { Controller } from 'react-hook-form';

import { TextInput } from '@/components/Form/TextInput';
import { ToggleGroup } from '@/components/Form/ToggleGroup';

import { useTranslate } from '@/hooks/useTranslate';

import { TaxNumberTypeCode } from '@/types/api/tax-info';

import { getDataTestId } from '@/utils/browser';
import { isNil } from '@/utils/common';

import { UsTaxReturnYesSectionProps } from '../TaxInfoDrawer.interface';

import { TaxInfoFieldLabel, TaxInfoSubSection } from '../TaxInfoDrawer.styled';

export function UsTaxReturnYesSection({
  control,
  errors,
  taxNumberTypeCode,
  isPending,
  resetField,
}: Readonly<UsTaxReturnYesSectionProps>) {
  const { t } = useTranslate();

  const handleTaxNumberTypeChange = (
    selectedValue: string | null,
    onChange: (value: TaxNumberTypeCode) => void,
  ) => {
    if (selectedValue === null) {
      return;
    }

    onChange(selectedValue as TaxNumberTypeCode);
    resetField('usTaxNumber', { defaultValue: '' });
  };

  return (
    <TaxInfoSubSection>
      <TaxInfoFieldLabel variant="caption">
        {t('taxInfoDrawer.question1.usTaxType.label')}
      </TaxInfoFieldLabel>
      <Controller
        control={control}
        name="taxNumberTypeCode"
        render={({ field: { value, onChange } }) => (
          <ToggleGroup
            fullWidth
            size="medium"
            name="taxNumberTypeCode"
            options={[
              { value: TaxNumberTypeCode.SSN, text: 'SSN' },
              { value: TaxNumberTypeCode.ITIN, text: 'ITIN' },
            ]}
            value={value ?? null}
            onChange={(_event, selectedValue) => {
              handleTaxNumberTypeChange(selectedValue, onChange);
            }}
            {...getDataTestId('tax-info-us-tax-type-toggle')}
          />
        )}
      />

      <TextInput
        inputMode="numeric"
        name="usTaxNumber"
        control={control}
        placeholder={t('taxInfoDrawer.question1.usTaxNumber.placeholder')}
        disabled={isNil(taxNumberTypeCode) || isPending}
        error={!!errors.usTaxNumber}
        helperText={errors.usTaxNumber?.message}
        {...getDataTestId('tax-info-us-tax-number-input')}
      />
    </TaxInfoSubSection>
  );
}
